import { useMemo } from "react";

import type { DayTripPlanI18nEditQuery } from "@/lib/gql/__generated__/graphql";

import { serverModelToInputModelConverter } from "./converter/server-model-to-input-mode/server-model-to-input-mode";
import { useSendCustomMailSettingForCheckInBefore_00days } from "../../../../../../_shared/model/repository/mutation/check-in-before-00-days";
import { useSendCustomMailSettingForCheckInBefore_01days } from "../../../../../../_shared/model/repository/mutation/check-in-before-01-days";
import { useSendCustomMailSettingForCheckInBefore_07days } from "../../../../../../_shared/model/repository/mutation/check-in-before-07-days";
import { useSendCustomMailSettingForCheckOutAfter_01days } from "../../../../../../_shared/model/repository/mutation/check-out-after-01-days";
import { useSendCustomMailSettingForReservationCancel } from "../../../../../../_shared/model/repository/mutation/reservation-cancel";
import { useSendCustomMailSettingForReservationCreate } from "../../../../../../_shared/model/repository/mutation/reservation-create";
import { useSendCustomMailSettingForReservationUpdate } from "../../../../../../_shared/model/repository/mutation/reservation-update";
import { useInitProductDayTripPlanI18nFormModelContext } from "../../../init";
import { useDayTripPlanI18nEditMutation } from "../../../repository/existing/mutation/use-plan-edit-mutation";

import type { ProductDayTripPlanI18nFormModel } from "../../..";
import type { ProductExistingDayTripPlanI18nFormModelContextType } from "../../../../ui/existing/context/context";
type Props = {
	serverModel: DayTripPlanI18nEditQuery;
};

export const useInitialized = ({ serverModel }: Props): ProductExistingDayTripPlanI18nFormModelContextType => {
	const inputModel = useMemo(() => serverModelToInputModelConverter(serverModel), [serverModel]);

	const { planCustomMailSettingI18n, basicCustomMailSettingI18n } = serverModel;
	const queryData = useMemo(() => {
		return {
			hotelEmailAddress: serverModel.hotel.email ?? "",
			hotelPolicy: {
				questions: serverModel.hotelPolicyI18n?.questions ?? [],
			},
			customEmailSetting: {
				reservation: {
					atCreated: serverModel.customEmailSettingI18n?.reservation?.atCreated ?? "",
					atUpdated: serverModel.customEmailSettingI18n?.reservation?.atUpdated ?? "",
					atCanceled: serverModel.customEmailSettingI18n?.reservation?.atCanceled ?? "",
					atCin: serverModel.customEmailSettingI18n?.reservation?.atCin ?? "",
				},
			},
			forReservation: serverModel.planCustomMailSettingI18n?.forReservation ?? {
				atCreated: {
					text: planCustomMailSettingI18n?.forReservation?.atCreated?.text ?? "",
					override: planCustomMailSettingI18n?.forReservation?.atCreated?.override ?? false,
				},
				atUpdated: {
					text: planCustomMailSettingI18n?.forReservation?.atUpdated?.text ?? "",
					override: planCustomMailSettingI18n?.forReservation?.atUpdated?.override ?? false,
				},
				atCanceled: {
					text: planCustomMailSettingI18n?.forReservation?.atCanceled?.text ?? "",
					override: planCustomMailSettingI18n?.forReservation?.atCanceled?.override ?? false,
				},
				atCheckInBefore_07days: {
					text: planCustomMailSettingI18n?.forReservation?.atCheckInBefore_07days?.text ?? "",
					shouldSendEmail:
						planCustomMailSettingI18n?.forReservation?.atCheckInBefore_07days?.shouldSendEmail ?? false,
					override: planCustomMailSettingI18n?.forReservation?.atCheckInBefore_07days?.override ?? false,
				},
				atCheckInBefore_01days: {
					text: planCustomMailSettingI18n?.forReservation?.atCheckInBefore_01days?.text ?? "",
					shouldSendEmail:
						planCustomMailSettingI18n?.forReservation?.atCheckInBefore_01days?.shouldSendEmail ?? false,
					override: planCustomMailSettingI18n?.forReservation?.atCheckInBefore_01days?.override ?? false,
				},
				atCheckInBefore_00days: {
					text: planCustomMailSettingI18n?.forReservation?.atCheckInBefore_00days?.text ?? "",
					shouldSendEmail:
						planCustomMailSettingI18n?.forReservation?.atCheckInBefore_00days?.shouldSendEmail ?? false,
					override: planCustomMailSettingI18n?.forReservation?.atCheckInBefore_00days?.override ?? false,
				},
				atCheckOutAfter_01days: {
					text: planCustomMailSettingI18n?.forReservation?.atCheckOutAfter_01days?.text ?? "",
					shouldSendEmail:
						planCustomMailSettingI18n?.forReservation?.atCheckOutAfter_01days?.shouldSendEmail ?? false,
					override: planCustomMailSettingI18n?.forReservation?.atCheckOutAfter_01days?.override ?? false,
				},
			},
			// 基本メール設定を追加
			basicCustomMailSetting: basicCustomMailSettingI18n?.forReservation,
			defaultPlanCustomMailSetting: planCustomMailSettingI18n?.forReservation,
		} satisfies ProductDayTripPlanI18nFormModel["QueryData"];
	}, [
		serverModel, //
		planCustomMailSettingI18n,
		basicCustomMailSettingI18n,
	]);

	const { onSetViewModel, onResetViewModel, viewModel } = useInitProductDayTripPlanI18nFormModelContext({
		inputModel,
		queryData,
	});

	const { onUpdate } = useDayTripPlanI18nEditMutation({ onResetViewModel });

	const { sendTestMailAtReservationCreate } = useSendCustomMailSettingForReservationCreate();
	const { sendTestMailAtReservationUpdate } = useSendCustomMailSettingForReservationUpdate();
	const { sendTestMailAtReservationCancel } = useSendCustomMailSettingForReservationCancel();
	const { sendTestMailAtCheckInBefore07Days } = useSendCustomMailSettingForCheckInBefore_07days();
	const { sendTestMailAtCheckInBefore01Days } = useSendCustomMailSettingForCheckInBefore_01days();
	const { sendTestMailAtCheckInBefore00Days } = useSendCustomMailSettingForCheckInBefore_00days();
	const { sendTestMailAtCheckOutAfter01Days } = useSendCustomMailSettingForCheckOutAfter_01days();
	return {
		queryData,
		onSetViewModel,
		viewModel,
		onUpdate: () => onUpdate({ ...viewModel }),

		testEmailSender: {
			sendTestMailAtReservationCreate,
			sendTestMailAtReservationUpdate,
			sendTestMailAtReservationCancel,
			sendTestMailAtCheckInBefore07Days,
			sendTestMailAtCheckInBefore01Days,
			sendTestMailAtCheckInBefore00Days,
			sendTestMailAtCheckOutAfter01Days,
		},
	};
};
